import React, { useState, useEffect, useRef } from 'react';
import BasketSidebar, { TreeItem } from '../components/BasketSidebar';
import ProfileSidebar from '../components/ProfileSidebar';
import TrackOrdersSidebar, { OrderItem } from '../components/TrackOrdersSidebar';
import Footer from '../components/Footer';
import JoinUpdates from '../components/JoinUpdates';
import Navbar from '../components/Navbar';
import AddressModal from '../components/AddressModal';
import DeleteAccountConfirmModal from '../components/DeleteAccountConfirmModal';
import DeleteAccountSuccessModal from '../components/DeleteAccountSuccessModal';
import PaymentSuccessModal from '../components/PaymentSuccessModal';
import { fetchWithAuth } from '../lib/fetchWithAuth';
import { useBasket } from '../hooks/useBasket';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAccountDeletion } from '../hooks/useAccountDeletion';
import { useAddressAutocomplete } from '../hooks/useAddressAutocomplete';

interface GardenPageProps {
    session: any;
    logout: () => void;
}

const GardenPage: React.FC<GardenPageProps> = ({ session, logout }) => {
    const [trees, setTrees] = useState<TreeItem[]>([]);
    const handleLogout = logout;

    const radiusOptions = [1, 2, 5, 10, 15, 20];
    const [radiusIndex, setRadiusIndex] = useState(2);
    const radius = radiusOptions[radiusIndex];

    // --- PROFILE ---
    const {
        isProfileOpen, setIsProfileOpen, isEditingProfile, setIsEditingProfile,
        profileName, setProfileName, profileAddress, profileCity, profileState, profileZipCode,
        saveLoading, fetchUserProfile, updateProfile,
    } = useUserProfile(handleLogout);
    const [userTreeCount, setUserTreeCount] = useState(0);
    const [userHomeLocation, setUserHomeLocation] = useState<{ latitude: number | null; longitude: number | null } | null>({ latitude: null, longitude: null });
    const [locationReady, setLocationReady] = useState(false);

    const fetchMarketplaceExcludingOwn = async (latitude: number, longitude: number) => {
        try {
            const marketplaceRes = await fetchWithAuth(`/api/plants/nearby?latitude=${latitude}&longitude=${longitude}&maxDistance=${radius * 1609}`);
            if (marketplaceRes.ok) {
                const marketplaceData = await marketplaceRes.json();
                setTrees(marketplaceData);
            }

            const ownPlantsRes = await fetchWithAuth('/api/plants');
            if (ownPlantsRes.ok) {
                const ownPlantsData = await ownPlantsRes.json();
                setUserTreeCount(ownPlantsData.length);
            }
        } catch (error) {
            console.error("Error communicating with local MongoDB backend:", error);
        }
    };

    // --- BASKET ---
    const {
        basket, isBasketOpen, setIsBasketOpen, flashBasket, checkoutLoading,
        clientSecret, setClientSecret, showPaymentSuccess, setShowPaymentSuccess,
        addToBasket, updateQuantity, removeFromBasket, handleCheckout, handlePaymentSuccess,
        totalItemsCount, grandTotalPrice,
    } = useBasket(() => {
        if (!userHomeLocation?.latitude || !userHomeLocation?.longitude) {
            fetchMarketplaceExcludingOwn(37.4457, -122.1619);
        } else {
            fetchMarketplaceExcludingOwn(userHomeLocation.latitude, userHomeLocation.longitude);
        }
    });

    // --- TRACK ORDERS ---
    const [isTrackOrdersOpen, setIsTrackOrdersOpen] = useState(false);
    const [userOrders, setUserOrders] = useState<OrderItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    // --- ACCOUNT DELETION ---
    const {
        showDeleteConfirm, setShowDeleteConfirm, showDeleteSuccess, deleteLoading, executeAccountDeletion,
    } = useAccountDeletion();

    // --- MAPS / ADDRESS ---
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [searchAddress, setSearchAddress] = useState('');
    const [addressError, setAddressError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [modalCity, setModalCity] = useState('');
    const [modalState, setModalState] = useState('');
    const [modalZipCode, setModalZipCode] = useState('');
    const [modalStreetAddress, setModalStreetAddress] = useState('');

    const mainAddressInputRef = useRef<HTMLInputElement | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useAddressAutocomplete(mainAddressInputRef, showAddressModal, (result) => {
        setUserHomeLocation({ latitude: result.latitude, longitude: result.longitude });
        setSearchAddress(result.formattedAddress);
        setModalStreetAddress(result.streetAddress);
        setModalCity(result.city);
        setModalState(result.state);
        setModalZipCode(result.zipCode);
    });

    const loadUserProfile = async () => {
        try {
            const data = await fetchUserProfile();
            if (data) {
                if (data.homeLocation?.latitude && data.homeLocation?.longitude) {
                    setUserHomeLocation({
                        latitude: data.homeLocation.latitude,
                        longitude: data.homeLocation.longitude
                    });
                } else {
                    setUserHomeLocation(null);
                }
            }
        } finally {
            setLocationReady(true);
        }
    };

    const handleAddressSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddressError(null);
        const streetTarget = modalStreetAddress || searchAddress;

        if (!streetTarget || !modalCity || !modalState || !modalZipCode || !userHomeLocation?.latitude || !userHomeLocation?.longitude) {
            setAddressError('Please select a valid structural address selection from the dropdown menu.');
            return;
        }

        setActionLoading(true);
        try {
            const response = await fetchWithAuth('/api/users/profile', {
                method: 'PUT',
                body: JSON.stringify({
                    address: streetTarget,
                    city: modalCity,
                    state: modalState,
                    zipCode: modalZipCode,
                    homeLocation: { latitude: userHomeLocation.latitude, longitude: userHomeLocation.longitude }
                })
            });

            if (response.ok) {
                setShowAddressModal(false);
                loadUserProfile();
                alert("Home context coordinates locked completely!");
            } else {
                throw new Error('Failed to save profile parameters.');
            }
        } catch (error: any) {
            setAddressError(error.message || 'Could not save address configuration.');
        } finally {
            setActionLoading(false);
        }
    };

    const fetchUserOrders = async () => {
        setOrdersLoading(true);
        try {
            const res = await fetchWithAuth('/api/orders');
            if (res.ok) {
                const data = await res.json();
                setUserOrders(data);
            }
        } catch (err) {
            console.error("Error retrieving account order parameters:", err);
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        if (session?.user?.id || session?.user?._id) {
            loadUserProfile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.user?.id, session?.user?._id]);

    useEffect(() => {
        if (!locationReady) return;
        if (userHomeLocation?.latitude && userHomeLocation?.longitude) {
            fetchMarketplaceExcludingOwn(userHomeLocation.latitude, userHomeLocation.longitude);
        } else {
            fetchMarketplaceExcludingOwn(37.4457, -122.1619);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationReady, userHomeLocation, radius]);

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const ok = await updateProfile({
                name: profileName,
                address: profileAddress,
                city: profileCity,
                state: profileState,
                zipCode: profileZipCode
            });
            if (!ok) {
                alert("Failed to update profile info on local server.");
            }
        } catch (err: any) {
            alert(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col justify-between font-sans">
            <Navbar
                session={session} totalItemsCount={totalItemsCount} flashBasket={flashBasket}
                isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen}
                onOpenTrackOrders={() => { setIsTrackOrdersOpen(true); fetchUserOrders(); }}
                onOpenBasket={() => setIsBasketOpen(true)}
                onOpenProfile={() => setIsProfileOpen(true)}
            />

            <div className="relative pt-28 pb-16 w-full bg-gradient-to-b from-white via-green-50/20 to-white overflow-hidden border-b border-gray-100/80">
                <div className="absolute inset-0 opacity-55 z-0 pointer-events-none">
                    <img src="/16.jpg" alt="Garden Backdrop" className="w-full h-full object-cover" />
                </div>
                <main className="relative z-10 container mx-auto px-4 max-w-6xl">

                    {userTreeCount > 0 ? (
                        <div className="w-full bg-white/90 backdrop-blur-xl rounded-3xl p-6 mb-10 border border-green-100 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-xl text-left">
                            <div className="flex items-center gap-4">
                                <span className="text-4xl">🌳</span>
                                <div>
                                    <h3 className="font-extrabold text-gray-900 text-xl" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Ready to grow your digital orchard?</h3>
                                    <p className="text-gray-600 text-sm mt-0.5">Add more trees to your garden.</p>
                                </div>
                            </div>
                            <button onClick={() => window.location.hash = '#/dashboard'} className="bg-green-600 text-white font-bold px-6 py-3 rounded-full hover:bg-green-700 shadow-md transition-all text-sm whitespace-nowrap">Manage your trees</button>
                        </div>
                    ) : (
                        <div className="w-full bg-white/90 backdrop-blur-xl rounded-3xl p-6 mb-10 border border-green-100 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-xl text-left">
                            <div className="flex items-center gap-4">
                                <span className="text-4xl">🌳</span>
                                <div>
                                    <h3 className="font-extrabold text-gray-900 text-xl" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Have fruit-bearing trees in your backyard?</h3>
                                    <p className="text-gray-600 text-sm mt-0.5">List your trees, manage pricing, and start earning extra passive income today.</p>
                                </div>
                            </div>
                            <button onClick={() => window.location.hash = '#/dashboard'} className="bg-green-600 text-white font-bold px-6 py-3 rounded-full hover:bg-green-700 shadow-md transition-all text-sm whitespace-nowrap">Want to become a seller?</button>
                        </div>
                    )}

                    {(!userHomeLocation?.latitude && !userHomeLocation?.longitude) ? (
                        <>
                        <h1 className="text-xl font-black text-gray-900 mb-8 tracking-tight text-left" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                            <button onClick={() => setShowAddressModal(true)} className="bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 shadow-md transition-all text-sm whitespace-nowrap">
                                Update address
                            </button>{" "}
                            for better experience
                        </h1>
                        <div className="mb-8 max-w-md">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-700">Search Radius</span>
                                    <span className="text-sm font-semibold text-green-600">{radius} mi</span>
                                </div>
                                <input
                                    type="range" min="0" max={radiusOptions.length - 1} step="1"
                                    value={radiusIndex} onChange={(e) => setRadiusIndex(Number(e.target.value))}
                                    className="w-full cursor-pointer"
                                />
                                <div className="flex justify-between mt-1 text-xs text-gray-500">
                                    {radiusOptions.map((value) => (
                                        <span key={value}>{value}mi</span>
                                    ))}
                                </div>
                            </div>
                            </>

                    ) : (
                        <>
                            <h1 className="text-3xl font-black text-gray-900 mb-4 tracking-tight text-left" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                                Fresh Local Produce Near You
                            </h1>
                            <div className="mb-8 max-w-md">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-700">Search Radius</span>
                                    <span className="text-sm font-semibold text-green-600">{radius} mi</span>
                                </div>
                                <input
                                    type="range" min="0" max={radiusOptions.length - 1} step="1"
                                    value={radiusIndex} onChange={(e) => setRadiusIndex(Number(e.target.value))}
                                    className="w-full cursor-pointer"
                                />
                                <div className="flex justify-between mt-1 text-xs text-gray-500">
                                    {radiusOptions.map((value) => (
                                        <span key={value}>{value}mi</span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {trees.length === 0 ? (
                        <div className="text-center py-20 bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-100 shadow-sm">
                            <div className="text-5xl mb-4">🥭</div><p className="text-gray-500 text-lg font-medium">No marketplace listings available at the moment.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {trees.map((tree) => (
                                <div key={tree._id} className="bg-white/95 rounded-2xl overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
                                    <div className="relative">
                                        <img src={tree.imageUrl?.startsWith('http') ? tree.imageUrl : (process.env.REACT_APP_API_URL || 'http://localhost:3000') + tree.imageUrl} alt={tree.name} className="w-full h-48 object-cover rounded-t-2xl" />
                                    </div>
                                    <div className="p-4 flex-grow flex flex-col justify-between">
                                        <div className="mb-4">
                                            <h3 className="font-extrabold text-gray-900 text-xl tracking-tight text-left">{tree.name}</h3>
                                            <p className="text-green-600 font-black text-lg mt-0.5 text-left">${tree.price} <span className="text-xs text-gray-400 font-medium">/ each</span></p>
                                        </div>
                                        <button onClick={() => addToBasket(tree)} className="w-full bg-green-600 text-white font-bold py-2.5 rounded-xl hover:bg-green-700 transition-colors shadow-md flex items-center justify-center gap-2 text-sm">Add to Basket</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>

            <JoinUpdates profileName={profileName} session={session} />
            <Footer />

            <BasketSidebar
                isOpen={isBasketOpen} onClose={() => setIsBasketOpen(false)}
                basket={basket} updateQuantity={updateQuantity} removeFromBasket={removeFromBasket}
                handleCheckout={handleCheckout} checkoutLoading={checkoutLoading}
                clientSecret={clientSecret} setClientSecret={setClientSecret}
                handlePaymentSuccess={handlePaymentSuccess} grandTotalPrice={grandTotalPrice}
            />

            <ProfileSidebar
                isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)}
                session={session} profileName={profileName} setProfileName={setProfileName}
                profileAddress={profileAddress} profileCity={profileCity}
                profileState={profileState} profileZipCode={profileZipCode}
                isEditingProfile={isEditingProfile} setIsEditingProfile={setIsEditingProfile}
                handleUpdateProfile={handleUpdateProfile} saveLoading={saveLoading}
                handleLogout={handleLogout} handleDeleteAccountClick={() => setShowDeleteConfirm(true)}
                userTreeCount={userTreeCount}
            />

            <TrackOrdersSidebar
                isOpen={isTrackOrdersOpen} onClose={() => setIsTrackOrdersOpen(false)}
                ordersLoading={ordersLoading} userOrders={userOrders}
            />

            <AddressModal
                show={showAddressModal} onClose={() => setShowAddressModal(false)} onSubmit={handleAddressSubmit}
                addressError={addressError} searchAddress={searchAddress} setSearchAddress={setSearchAddress}
                modalCity={modalCity} setModalCity={setModalCity} modalState={modalState} setModalState={setModalState}
                modalZipCode={modalZipCode} setModalZipCode={setModalZipCode} actionLoading={actionLoading}
                inputRef={mainAddressInputRef}
            />

            <DeleteAccountConfirmModal
                show={showDeleteConfirm} onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={executeAccountDeletion} loading={deleteLoading}
            />

            <DeleteAccountSuccessModal show={showDeleteSuccess} onAcknowledge={handleLogout} />

            <PaymentSuccessModal show={showPaymentSuccess} onClose={() => setShowPaymentSuccess(false)} />
        </div>
    );
};

export default GardenPage;
